import { Component, DestroyRef, ElementRef, OnInit, ViewChild } from '@angular/core';
import { DatePipe, NgClass, NgFor, NgIf } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { MatSortModule, Sort } from '@angular/material/sort';
import { debounceTime, distinctUntilChanged, Subject, switchMap, tap } from 'rxjs';
import { Offcanvas } from 'bootstrap';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BreadcrumbComponent } from '../../elements/breadcrumb/breadcrumb.component';
import { PaginationComponent } from '../../elements/pagination/pagination.component';
import { HttpServiceService } from '../../services/http-service.service';
import { LoadingService } from '../../services/loading.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { NgxToastrService } from '../../_services/ngx-toastr/ngx-toastr.service';
import { USER_PERMISSIONS } from '../../app.constants';
import { Lead, LeadFormData, LEAD_STAGE_OPTIONS, LEAD_STATUS_OPTIONS, LEAD_SERVICE_OPTIONS, leadSourceClass, leadSourceWebsiteClass } from '../lead.model';
import { NgSelectModule } from '@ng-select/ng-select';
import { Select2Data } from 'ng-select2-component';

@Component({
  selector: 'app-leads-list',
  standalone: true,
  imports: [
    NgIf,
    NgFor,
    NgClass,
    DatePipe,
    FormsModule,
    ReactiveFormsModule,
    BreadcrumbComponent,
    PaginationComponent,
    MatSortModule,
    NgSelectModule
  ],
  templateUrl: './leads-list.component.html',
  styleUrls: ['./leads-list.component.css']
})
export class LeadsListComponent implements OnInit {
  @ViewChild('leadModal') leadModalElement!: ElementRef;
  private leadModalInstance: Offcanvas | null = null;

  breadcrumbList = {
    breadcrumb_path: 'Dashboard',
    currentURL: 'Leads',
    breadcrumb_url: '/admin'
  };

  leads: Lead[] = [];
  totalLeads: number = 0;
  page: number = 1;
  limit: number = 50;
  totalPage: number = 1;
  totalRows: number = 50;
  sortBy: string = 'created_at';
  orderBy: string = 'DESC';

  // Search form
  searchForm!: FormGroup;
  disableClear: boolean = false;

  // Modal state
  showLeadModal: boolean = false;
  isEditMode: boolean = false;
  selectedLeadId: string = '';
  leadForm!: FormGroup;

  // Dropdown options
  sourceOptions = [
    { value: 'Website/Form', label: 'Website/Form' },
    { value: 'Internal Referral', label: 'Internal Referral' },
    { value: 'Direct Call/Message', label: 'Direct Call/Message' },
    { value: 'Whatsapp', label: 'Whatsapp' },
    { value: 'Email', label: 'Email' },
    { value: 'Epoque(Lead Generator)', label: 'Epoque(Lead Generator)' },
    { value: 'Others', label: 'Others' }
  ];

  stageOptions = LEAD_STAGE_OPTIONS;
  statusOptions = LEAD_STATUS_OPTIONS;
  serviceOptions = LEAD_SERVICE_OPTIONS;

  // Assigned To dropdown with lazy loading
  assignedToUsers: Select2Data = [];
  usersList: any[] = [];
  searchUsers$ = new Subject<string>();
  private userPage = 1;
  private userPageSize = 50;
  private hasMoreUsers = true;

  // Permissions
  USER_PERMISSIONS = USER_PERMISSIONS;
  canAdd: boolean = false;
  canUpdate: boolean = false;
  canDelete: boolean = false;

  constructor(
    private httpService: HttpServiceService,
    public loadingService: LoadingService,
    private localStorageService: LocalStorageService,
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private alertService: NgxToastrService,
    private destroyRef: DestroyRef
  ) {
    this.initForms();
  }

  ngOnInit(): void {
    this.checkPermissions();
    this.setupSearchListener();
    this.setupUserSearch();
    // Coming back from the view page: the filters were carried out in the URL,
    // so re-apply them and fetch the same slice the user left.
    const params = this.route.snapshot.queryParams;
    if (params['page']) {
      this.restoreFilters(params);
      this.handleSearch('keepPage');
    } else {
      this.getAllLeads();
    }
  }

  /**
   * Filter state to carry into the view page so Back returns to the same
   * filtered page.
   */
  private currentFilterState(): Params {
    const state: Params = { page: this.page, sortBy: this.sortBy, orderBy: this.orderBy };
    const searchData = this.searchForm.value;
    for (const key of Object.keys(searchData)) {
      if (searchData[key]) state[key] = searchData[key];
    }
    return state;
  }

  private restoreFilters(params: Params): void {
    this.page = parseInt(params['page'], 10) || 1;
    this.sortBy = params['sortBy'] || this.sortBy;
    this.orderBy = params['orderBy'] || this.orderBy;
    this.searchForm.patchValue(
      {
        search: params['search'] ?? '',
        source: params['source'] ?? '',
        stage: params['stage'] ?? '',
        status: params['status'] ?? '',
        assigned_to: params['assigned_to'] ?? ''
      },
      { emitEvent: false }
    );
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      const modalElement = this.leadModalElement?.nativeElement;
      if (modalElement) {
        this.leadModalInstance = Offcanvas.getOrCreateInstance(modalElement, {
          backdrop: 'static',
          keyboard: false
        });
      }
    });
  }

  initForms(): void {
    // Search form
    this.searchForm = this.fb.group({
      search: [''],
      source: [''],
      stage: [''],
      status: [''],
      assigned_to: ['']
    });

    // Lead form with only source and name required
    this.leadForm = this.fb.group({
      source: ['', Validators.required],
      name: ['', Validators.required],
      property_type: [''],
      email: ['', Validators.email],
      phone: [''],
      locality: [''],
      city: [''],
      state: [''],
      follow_up_date: [''],
      customer_location: [''],
      customer_city: [''],
      customer_state: [''],
      looking_for: [''],
      service: [''],
      stage: [''],
      status: [''],
      assigned_to: [''],
      maps_link: ['']
    });
  }

  // ── Attachments ────────────────────────────────────────────────────────
  // 20MB per file; images / PDF / DOCX / CSV / Excel; multiple files allowed.
  selectedFiles: File[] = [];
  existingUploads: any[] = [];
  readonly maxFileSizeMb = 20;
  readonly acceptAttr = 'image/*,.pdf,.doc,.docx,.csv,.xls,.xlsx';
  private readonly allowedMimes = [
    'image/jpeg', 'image/png', 'image/jpg', 'image/webp',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/csv', 'application/csv',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ];

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    const maxBytes = this.maxFileSizeMb * 1024 * 1024;

    Array.from(input.files).forEach((file) => {
      const ext = file.name.split('.').pop()?.toLowerCase() || '';
      const typeOk =
        this.allowedMimes.includes(file.type) ||
        ['jpg', 'jpeg', 'png', 'webp', 'pdf', 'doc', 'docx', 'csv', 'xls', 'xlsx'].includes(ext);
      if (!typeOk) {
        this.alertService.error(`"${file.name}" is not an allowed file type`, 'toast-top-right');
        return;
      }
      if (file.size > maxBytes) {
        this.alertService.error(
          `"${file.name}" exceeds the ${this.maxFileSizeMb}MB limit`,
          'toast-top-right'
        );
        return;
      }
      this.selectedFiles.push(file);
    });

    // Reset so re-selecting the same file fires (change) again.
    input.value = '';
  }

  removeFile(index: number): void {
    this.selectedFiles.splice(index, 1);
  }

  setupSearchListener(): void {
    this.searchForm.get('search')?.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.handleSearch('');
      });
  }

  setupUserSearch(): void {
    this.searchUsers$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap(() => {
          this.userPage = 1;
          this.hasMoreUsers = true;
        }),
        switchMap((term) => {
          return this.httpService.getUsersByPropertyRole(
            this.userPage,
            this.userPageSize,
            term
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((response) => {
        this.usersList = response.data?.users ?? [];
        this.hasMoreUsers =
          this.usersList.length < (response.data?.total ?? 0);
        this.assignedToUsers = this.usersList.map((user) => ({
          value: user.uid,
          label: `${user.first_name} ${user.last_name}`,
          data: user,
        }));
      });
    // Initial load
    this.searchUsers$.next('');
  }

  onUserScrollEnd(): void {
    if (!this.hasMoreUsers) return;
    this.userPage++;
    this.httpService
      .getUsersByPropertyRole(this.userPage, this.userPageSize)
      .subscribe((resp) => {
        const more = resp.data?.users ?? [];
        this.hasMoreUsers =
          this.usersList.length + more.length < (resp.data?.total ?? 0);
        this.usersList = [...this.usersList, ...more];
        this.assignedToUsers = this.usersList.map((user) => ({
          value: user.uid,
          label: `${user.first_name} ${user.last_name}`,
          data: user,
        }));
      });
  }

  checkPermissions(): void {
    this.canAdd = this.localStorageService.checkPermission(USER_PERMISSIONS.LEADS.LEADS_ADD);
    this.canUpdate = this.localStorageService.checkPermission(USER_PERMISSIONS.LEADS.LEADS_UPDATE);
    this.canDelete = this.localStorageService.checkPermission(USER_PERMISSIONS.LEADS.LEADS_DELETE);
  }

  handleSearch(keepPage: string = ''): void {
    const searchData = this.searchForm.value;
    let queryParams = '';

    if (searchData.search) {
      queryParams += `&search=${searchData.search}`;
      this.disableClear = true;
    }
    if (searchData.source) {
      queryParams += `&source=${searchData.source}`;
      this.disableClear = true;
    }
    if (searchData.stage) {
      queryParams += `&stage=${searchData.stage}`;
      this.disableClear = true;
    }
    if (searchData.status) {
      queryParams += `&status=${searchData.status}`;
      this.disableClear = true;
    }
    if (searchData.assigned_to) {
      queryParams += `&assigned_to=${searchData.assigned_to}`;
      this.disableClear = true;
    }

    if (keepPage !== 'keepPage') {
      this.page = 1;
    }

    this.getAllLeads(queryParams);
  }

  getAllLeads(queryParams: string = ''): void {
    this.loadingService.show();

    this.httpService
      .getAllLeads(this.page, this.limit, this.sortBy, this.orderBy, queryParams)
      .subscribe({
        next: (response: any) => {
          this.loadingService.hide();
          if (response.success) {
            this.leads = response.data.leads;
            this.totalLeads = response.data.total;
            this.totalPage = response.data.pageCount;
            this.totalRows = this.leads.length > this.limit ? this.limit : this.leads.length;
          }
        },
        error: (error) => {
          this.loadingService.hide();
          console.error('Error fetching leads:', error);
        }
      });
  }

  sortData(sort: Sort): void {
    if (sort.direction) {
      this.sortBy = sort.active;
      this.orderBy = sort.direction.toUpperCase();
    } else {
      this.sortBy = 'created_at';
      this.orderBy = 'DESC';
    }
    this.page = 1;
    this.handleSearch('');
  }

  pageChange(newPage: number): void {
    if (this.page == newPage) return;
    this.page = newPage;
    this.handleSearch('keepPage');
  }

  resetSearchForm(): void {
    this.searchForm.reset();
    this.page = 1;
    this.router.navigate([], { relativeTo: this.route, queryParams: {} });
    this.getAllLeads();
    this.disableClear = false;
  }

  viewLead(id: string): void {
    this.router.navigate(['/admin/leads/view', id], { queryParams: this.currentFilterState() });
  }

  openLeadModal(leadId?: string): void {
    if (leadId) {
      // Edit mode
      this.isEditMode = true;
      this.selectedLeadId = leadId;
      this.loadLeadDetails(leadId);
    } else {
      // Create mode
      this.isEditMode = false;
      this.selectedLeadId = '';
      this.leadForm.reset();
    }

    this.selectedFiles = [];
    this.existingUploads = [];
    this.showLeadModal = true;
    if (this.leadModalInstance) {
      this.leadModalInstance.show();
    }
  }

  closeLeadModal(): void {
    this.showLeadModal = false;
    this.leadForm.reset();
    this.selectedFiles = [];
    this.existingUploads = [];
    this.isEditMode = false;
    this.selectedLeadId = '';
    if (this.leadModalInstance) {
      this.leadModalInstance.hide();
    }
  }

  loadLeadDetails(id: string): void {
    this.loadingService.show();
    this.httpService.getLeadDetailsById(id).subscribe({
      next: (response: any) => {
        this.loadingService.hide();
        if (response.success) {
          const leadData = response.data;

          // Handle assigned_user object if present
          if (leadData.assigned_user && leadData.assigned_user.id) {
            // Add the assigned user to the dropdown list if not already present
            const userExists = this.assignedToUsers.find((u: any) => u.value === leadData.assigned_user.id);
            if (!userExists) {
              this.assignedToUsers = [
                {
                  value: leadData.assigned_user.id,
                  label: leadData.assigned_user.name,
                  data: leadData.assigned_user,
                },
                ...this.assignedToUsers
              ];
            }
            // Set the assigned_to field to the user's ID
            leadData.assigned_to = leadData.assigned_user.id;
          }

          this.existingUploads = leadData.uploads ?? [];
          this.leadForm.patchValue(leadData);
        }
      },
      error: () => {
        this.loadingService.hide();
        // Error toast is handled by HTTP interceptor
      }
    });
  }

  deleteExistingUpload(uid: string): void {
    if (!confirm('Delete this attachment?')) return;
    this.loadingService.show();
    this.httpService.deleteLeadUpload(uid).subscribe({
      next: (response: any) => {
        this.loadingService.hide();
        if (response.success) {
          this.existingUploads = this.existingUploads.filter((u) => u.id !== uid);
          this.alertService.success(response.message, 'toast-top-right');
        }
      },
      error: () => {
        this.loadingService.hide();
      }
    });
  }

  onSubmit(): void {
    if (this.leadForm.valid) {
      const values: LeadFormData = { ...this.leadForm.value };

      // Remove stage/status for create mode (defaults set by server)
      if (!this.isEditMode) {
        delete values.stage;
        delete values.status;
      }

      // When files are attached, send everything as multipart so the lead and
      // its attachments are saved in one request; otherwise keep JSON.
      const payload: any = this.selectedFiles.length > 0
        ? this.buildLeadFormData(values)
        : values;

      this.loadingService.show();

      const apiCall = this.isEditMode
        ? this.httpService.updateLead(this.selectedLeadId, payload)
        : this.httpService.createLead(payload);

      apiCall.subscribe({
        next: (response: any) => {
          this.loadingService.hide();
          if (response.success) {
            this.alertService.success(response.message, 'toast-top-right');
            this.closeLeadModal();
            this.getAllLeads();
          }
        },
        error: () => {
          this.loadingService.hide();
          // Error toast is handled by HTTP interceptor
        }
      });
    } else {
      this.markFormGroupTouched(this.leadForm);
    }
  }

  markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  private buildLeadFormData(values: Record<string, any>): FormData {
    const fd = new FormData();
    Object.keys(values).forEach((key) => {
      const value = values[key];
      if (value !== null && value !== undefined && value !== '') {
        fd.append(key, value);
      }
    });
    // Field name must match the server upload config (LEAD.UPLOADS.name = 'files').
    this.selectedFiles.forEach((file) => fd.append('files', file));
    return fd;
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.leadForm.get(fieldName);
    return !!(field && field.invalid && field.touched);
  }

  deleteLead(id: string): void {
    if (confirm('Are you sure you want to delete this lead?')) {
      this.loadingService.show();
      this.httpService.deleteLead(id).subscribe({
        next: (response: any) => {
          this.loadingService.hide();
          if (response.success) {
            this.alertService.success(response.message, 'toast-top-right');
            this.getAllLeads();
          }
        },
        error: () => {
          this.loadingService.hide();
          // Error toast is handled by HTTP interceptor
        }
      });
    }
  }

  sourceClass = leadSourceClass;
  websiteClass = leadSourceWebsiteClass;

  isFollowUpSoon(followUpDate: string | null): boolean {
    if (!followUpDate) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);

    const followUp = new Date(followUpDate);
    followUp.setHours(0, 0, 0, 0);

    const todayTime = today.getTime();
    const tomorrowTime = tomorrow.getTime();
    const followUpTime = followUp.getTime();

    return followUpTime === todayTime || followUpTime === tomorrowTime;
  }
}
